import React, { useCallback } from "react";
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
  Truck,
  Wallet,
  CreditCard,
  Handshake,
  Wrench,
  Building2,
  Monitor,
  Shield,
  Cpu,
  Headset,
  RotateCcw,
  Package,
  Archive,
  MapPin,
  Phone,
  Mail,
  Store,
  HelpCircle,
  FileText,
  List,
  BarChart,
  LayoutGrid,
  ListChecks,
  ShoppingCart,
  Users,
  Settings,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Info,
  Search,
  Star,
  Globe,
  Lock,
  Cookie,
  Scale,
  BookOpen,
  Briefcase,
  ShieldCheck,
} from "lucide-react";
import HtmlEditor from "./HtmlEditor";

const HERO_ICONS: Record<
  string,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  Truck,
  Wallet,
  CreditCard,
  Handshake,
  Wrench,
  Building2,
  Monitor,
  Shield,
  Cpu,
  Headset,
  RotateCcw,
  Package,
  Archive,
  MapPin,
  Phone,
  Mail,
  Store,
  HelpCircle,
  FileText,
  List,
  BarChart,
  LayoutGrid,
  ListChecks,
  ShoppingCart,
  Users,
  Settings,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Info,
  Search,
  Star,
  Globe,
  Lock,
  Cookie,
  Scale,
  BookOpen,
  Briefcase,
  ShieldCheck,
};
const HERO_ICON_NAMES = Object.keys(HERO_ICONS).sort();

function coerceString(val: unknown, fallback = ""): string {
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  return fallback;
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

function TextField({ label, value = "", onChange }: FieldProps) {
  const id = React.useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );
}

function TextareaField({
  label,
  value = "",
  onChange,
  rows = 3,
  monospace,
}: FieldProps & { rows?: number; monospace?: boolean }) {
  const id = React.useId();
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>
      <textarea
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${monospace ? "font-mono text-sm" : "text-sm"}`}
      />
    </div>
  );
}

function StringArrayEditor({
  items,
  onChange,
  placeholder = "Item",
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
}) {
  const addItem = useCallback(() => {
    onChange([...items, ""]);
  }, [items, onChange]);

  const removeItem = useCallback(
    (index: number) => {
      onChange(items.filter((_, i) => i !== index));
    },
    [items, onChange],
  );

  const updateItem = useCallback(
    (index: number, value: string) => {
      onChange(items.map((item, i) => (i === index ? value : item)));
    },
    [items, onChange],
  );

  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 items-start">
            <input
              type="text"
              value={item}
              onChange={(e) => updateItem(i, e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={placeholder}
            />
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="mt-2 text-red-500 hover:text-red-700 shrink-0"
              aria-label="Remover item"
              title="Remover item"
            >
              <Trash2 size={16} />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={addItem}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
      >
        <Plus size={14} /> Adicionar item
      </button>
    </div>
  );
}

function ObjectArrayEditor({
  items,
  onChange,
  fields,
  defaultItem,
  itemLabel,
}: {
  items: Record<string, unknown>[];
  onChange: (items: Record<string, unknown>[]) => void;
  fields: {
    key: string;
    label: string;
    type: "text" | "textarea" | "string_array" | "boolean" | "html";
  }[];
  defaultItem: Record<string, unknown>;
  itemLabel?: string;
}) {
  const containerId = React.useId();
  const [expandedIndex, setExpandedIndex] = React.useState<number | null>(null);

  const addItem = useCallback(() => {
    onChange([...items, { ...defaultItem }]);
  }, [items, onChange, defaultItem]);

  const removeItem = useCallback(
    (index: number) => {
      onChange(items.filter((_, i) => i !== index));
    },
    [items, onChange],
  );

  const updateField = useCallback(
    (index: number, key: string, value: string | string[] | boolean) => {
      onChange(
        items.map((item, i) =>
          i === index ? { ...item, [key]: value } : item,
        ),
      );
    },
    [items, onChange],
  );

  const moveItem = useCallback(
    (index: number, direction: "up" | "down") => {
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= items.length) return;
      const copy = [...items];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      onChange(copy);
    },
    [items, onChange],
  );

  return (
    <div className="space-y-3">
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li
            key={i}
            className="p-3 border border-gray-200 rounded-md bg-gray-50"
          >
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <button
                type="button"
                onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}
                className="flex items-center gap-2 flex-1 text-left min-w-0"
                aria-expanded={expandedIndex === i}
              >
                <span className="shrink-0">
                  {expandedIndex === i ? (
                    <ChevronUp size={14} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={14} className="text-gray-400" />
                  )}
                </span>
                <span className="text-xs font-medium text-gray-500 truncate">
                  {itemLabel || "Item"} {i + 1}
                  {expandedIndex !== i && item.question
                    ? ` — ${String(item.question).substring(0, 60)}`
                    : expandedIndex !== i && item.title
                      ? ` — ${String(item.title).substring(0, 60)}`
                      : expandedIndex !== i && item.name
                        ? ` — ${String(item.name).substring(0, 60)}`
                        : ""}
                </span>
              </button>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => moveItem(i, "up")}
                disabled={i === 0}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={`Mover ${(itemLabel || "item").toLowerCase()} ${i + 1} para cima`}
                title="Mover para cima"
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => moveItem(i, "down")}
                disabled={i === items.length - 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={`Mover ${(itemLabel || "item").toLowerCase()} ${i + 1} para baixo`}
                title="Mover para baixo"
              >
                <ArrowDown size={14} />
              </button>
              <button
                type="button"
                onClick={() => removeItem(i)}
                className="text-red-500 hover:text-red-700"
                aria-label={`Remover ${(itemLabel || "item").toLowerCase()} ${i + 1}`}
                title="Remover"
              >
                <Trash2 size={14} />
              </button>
            </div>
            {expandedIndex === i && (
              <div className="px-3 pb-3 space-y-2">
                {fields.map((field) => {
                  const fieldId = `${containerId}-${i}-${field.key}`;
                  if (field.type === "string_array") {
                    return (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          {field.label}
                        </label>
                        <StringArrayEditor
                          items={
                            Array.isArray(item[field.key])
                              ? (item[field.key] as string[])
                              : []
                          }
                          onChange={(v) => updateField(i, field.key, v)}
                          placeholder={field.label}
                        />
                      </div>
                    );
                  }
                  if (field.type === "html") {
                    return (
                      <div key={field.key} className="pt-1">
                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                          {field.label}
                        </label>
                        <HtmlEditor
                          value={coerceString(item[field.key])}
                          onChange={(v) => updateField(i, field.key, v)}
                          label=""
                        />
                      </div>
                    );
                  }
                  if (field.type === "boolean") {
                    return (
                      <div
                        key={field.key}
                        className="flex items-center gap-2 pt-1"
                      >
                        <input
                          id={fieldId}
                          type="checkbox"
                          checked={!!item[field.key]}
                          onChange={(e) =>
                            updateField(i, field.key, e.target.checked)
                          }
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label
                          htmlFor={fieldId}
                          className="text-xs font-medium text-gray-600 cursor-pointer"
                        >
                          {field.label}
                        </label>
                      </div>
                    );
                  }
                  return field.type === "textarea" ? (
                    <div key={field.key}>
                      <label
                        htmlFor={fieldId}
                        className="block text-xs font-medium text-gray-600 mb-0.5"
                      >
                        {field.label}
                      </label>
                      <textarea
                        id={fieldId}
                        value={coerceString(item[field.key])}
                        onChange={(e) =>
                          updateField(i, field.key, e.target.value)
                        }
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  ) : (
                    <div key={field.key}>
                      <label
                        htmlFor={fieldId}
                        className="block text-xs font-medium text-gray-600 mb-0.5"
                      >
                        {field.label}
                      </label>
                      <input
                        id={fieldId}
                        type="text"
                        value={coerceString(item[field.key])}
                        onChange={(e) =>
                          updateField(i, field.key, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={addItem}
        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
      >
        <Plus size={14} /> Adicionar item
      </button>
    </div>
  );
}

const STORE_FIELDS = [
  { key: "name", label: "Nome", type: "text" as const },
  { key: "intro", label: "Introdução", type: "text" as const },
  { key: "address", label: "Morada", type: "textarea" as const },
  { key: "phones", label: "Telefones", type: "string_array" as const },
  { key: "emails", label: "Emails", type: "string_array" as const },
  { key: "hours", label: "Horário", type: "string_array" as const },
];

export default function SectionFieldEditor({
  type,
  data,
  onChange,
}: {
  type: string;
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}) {
  const setField = useCallback(
    (key: string, value: unknown) => {
      onChange({ ...data, [key]: value });
    },
    [data, onChange],
  );

  switch (type) {
    case "hero":
      return (
        <div className="space-y-3">
          <TextField
            label="Título"
            value={coerceString(data.heading)}
            onChange={(v) => setField("heading", v)}
          />
          <TextField
            label="Subtítulo"
            value={coerceString(data.subtext)}
            onChange={(v) => setField("subtext", v)}
          />
          <TextField
            label="Atualizado em"
            value={coerceString(data.lastUpdated)}
            onChange={(v) => setField("lastUpdated", v)}
          />
          <TextField
            label="Imagem de banner (URL)"
            value={coerceString(data.image)}
            onChange={(v) => setField("image", v)}
          />
          <TextField
            label="Texto alternativo da imagem"
            value={coerceString(data.imageAlt)}
            onChange={(v) => setField("imageAlt", v)}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ícone
            </label>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center shrink-0">
                {(() => {
                  const IconComp = data.icon
                    ? HERO_ICONS[data.icon as string]
                    : null;
                  return IconComp ? (
                    <IconComp size={22} className="text-primary" />
                  ) : (
                    <span className="text-gray-300 text-xs">?</span>
                  );
                })()}
              </div>
              <select
                value={coerceString(data.icon)}
                onChange={(e) => setField("icon", e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">Sem ícone</option>
                {HERO_ICON_NAMES.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      );

    case "richtext":
      return (
        <HtmlEditor
          label="HTML"
          value={coerceString(data.html)}
          onChange={(v) => setField("html", v)}
        />
      );

    case "faq_list":
      return (
        <ObjectArrayEditor
          items={
            Array.isArray(data.items)
              ? (data.items as Record<string, unknown>[])
              : []
          }
          onChange={(items) => setField("items", items)}
          fields={[
            { key: "question", label: "Pergunta", type: "text" },
            { key: "answer", label: "Resposta", type: "html" },
          ]}
          defaultItem={{ question: "", answer: "" }}
          itemLabel="Pergunta"
        />
      );

    case "sections_list":
      return (
        <ObjectArrayEditor
          items={
            Array.isArray(data.items)
              ? (data.items as Record<string, unknown>[])
              : []
          }
          onChange={(items) => setField("items", items)}
          fields={[
            { key: "title", label: "Título", type: "text" },
            { key: "body", label: "Corpo", type: "html" },
          ]}
          defaultItem={{ title: "", body: "" }}
          itemLabel="Secção"
        />
      );

    case "stats_grid":
      return (
        <ObjectArrayEditor
          items={
            Array.isArray(data.items)
              ? (data.items as Record<string, unknown>[])
              : []
          }
          onChange={(items) => setField("items", items)}
          fields={[
            { key: "label", label: "Rótulo", type: "text" },
            { key: "value", label: "Valor", type: "text" },
          ]}
          defaultItem={{ label: "", value: "" }}
          itemLabel="Estatística"
        />
      );

    case "contact_info":
      return (
        <div className="space-y-3">
          <TextField
            label="Telefone"
            value={coerceString(data.phone)}
            onChange={(v) => setField("phone", v)}
          />
          <TextField
            label="Nota do telefone"
            value={coerceString(data.phoneNote)}
            onChange={(v) => setField("phoneNote", v)}
          />
          <TextField
            label="Email"
            value={coerceString(data.email)}
            onChange={(v) => setField("email", v)}
          />
          <TextareaField
            label="Morada"
            value={coerceString(data.address)}
            onChange={(v) => setField("address", v)}
          />
        </div>
      );

    case "stores_list":
      return (
        <div className="space-y-3">
          <TextField
            label="Introdução"
            value={coerceString(data.intro)}
            onChange={(v) => setField("intro", v)}
          />
          <TextField
            label="Nota de rodapé"
            value={coerceString(data.footnote)}
            onChange={(v) => setField("footnote", v)}
          />
          <ObjectArrayEditor
            items={
              Array.isArray(data.items)
                ? (data.items as Record<string, unknown>[])
                : []
            }
            onChange={(items) => setField("items", items)}
            fields={STORE_FIELDS}
            defaultItem={{
              name: "",
              intro: "",
              address: "",
              phones: [],
              emails: [],
              hours: [],
            }}
            itemLabel="Loja"
          />
        </div>
      );

    case "services_grid":
      return (
        <div className="space-y-3">
          <TextField
            label="Título da secção"
            value={coerceString(data.heading)}
            onChange={(v) => setField("heading", v)}
          />
          <ObjectArrayEditor
            items={
              Array.isArray(data.items)
                ? (data.items as Record<string, unknown>[])
                : []
            }
            onChange={(items) => setField("items", items)}
            fields={[
              { key: "title", label: "Título", type: "text" },
              { key: "description", label: "Descrição", type: "textarea" },
              { key: "icon", label: "Ícone", type: "text" },
            ]}
            defaultItem={{ title: "", description: "", icon: "" }}
            itemLabel="Serviço"
          />
        </div>
      );

    case "payment_methods":
      return (
        <div className="space-y-3">
          <TextField
            label="Introdução"
            value={coerceString(data.intro)}
            onChange={(v) => setField("intro", v)}
          />
          <ObjectArrayEditor
            items={
              Array.isArray(data.items)
                ? (data.items as Record<string, unknown>[])
                : []
            }
            onChange={(items) => setField("items", items)}
            fields={[
              { key: "title", label: "Nome", type: "text" },
              { key: "html", label: "Descrição (HTML)", type: "html" },
              { key: "soon", label: "Em Breve", type: "boolean" },
            ]}
            defaultItem={{ title: "", html: "", soon: false }}
            itemLabel="Método"
          />
        </div>
      );

    case "bullet_list":
      return (
        <StringArrayEditor
          items={Array.isArray(data.items) ? (data.items as string[]) : []}
          onChange={(items) => setField("items", items)}
          placeholder="Item da lista"
        />
      );

    case "product_grid": {
      const categories = Array.isArray(data.categories)
        ? (data.categories as unknown[]).filter(
            (c): c is string => typeof c === "string",
          )
        : [];
      return (
        <div className="space-y-3">
          <TextField
            label="Título da secção"
            value={coerceString(data.heading)}
            onChange={(v) => setField("heading", v)}
          />
          <TextField
            label="Subtítulo"
            value={coerceString(data.subtext)}
            onChange={(v) => setField("subtext", v)}
          />
          <TextField
            label="Marca (ex.: RAPTOR)"
            value={coerceString(data.brand)}
            onChange={(v) => setField("brand", v)}
          />
          <TextField
            label="Categorias (separadas por vírgula)"
            value={categories.join(", ")}
            onChange={(v) =>
              setField(
                "categories",
                v
                  .split(",")
                  .map((c) => c.trim())
                  .filter(Boolean),
              )
            }
          />
          <TextField
            label="Nº máximo de máquinas"
            value={
              data.limit === undefined || data.limit === null
                ? ""
                : String(data.limit)
            }
            onChange={(v) => {
              const n = Number(v);
              setField(
                "limit",
                v.trim() === "" || Number.isNaN(n) ? undefined : n,
              );
            }}
          />
          <TextField
            label="Link do botão"
            value={coerceString(data.ctaHref)}
            onChange={(v) => setField("ctaHref", v)}
          />
          <TextField
            label="Texto do botão"
            value={coerceString(data.ctaLabel)}
            onChange={(v) => setField("ctaLabel", v)}
          />
        </div>
      );
    }

    case "hero_banner":
      return (
        <div className="space-y-3">
          <TextField
            label="Badge"
            value={coerceString(data.badge)}
            onChange={(v) => setField("badge", v)}
          />
          <TextField
            label="Título"
            value={coerceString(data.heading)}
            onChange={(v) => setField("heading", v)}
          />
          <TextareaField
            label="Subtítulo"
            value={coerceString(data.subtext)}
            onChange={(v) => setField("subtext", v)}
          />
          <TextField
            label="Imagem (URL)"
            value={coerceString(data.image)}
            onChange={(v) => setField("image", v)}
          />
          <TextField
            label="Texto alternativo da imagem"
            value={coerceString(data.imageAlt)}
            onChange={(v) => setField("imageAlt", v)}
          />
          <TextField
            label="Link do botão"
            value={coerceString(data.ctaHref)}
            onChange={(v) => setField("ctaHref", v)}
          />
          <TextField
            label="Texto do botão"
            value={coerceString(data.ctaLabel)}
            onChange={(v) => setField("ctaLabel", v)}
          />
          <TextField
            label="Nota de confiança"
            value={coerceString(data.trustNote)}
            onChange={(v) => setField("trustNote", v)}
          />
          <TextField
            label="Cor de destaque (orange|red|amber|emerald|cyan)"
            value={coerceString(data.accent)}
            onChange={(v) => setField("accent", v)}
          />
        </div>
      );

    case "callout":
      return (
        <div className="space-y-3">
          <TextField
            label="Ícone"
            value={coerceString(data.icon)}
            onChange={(v) => setField("icon", v)}
          />
          <TextField
            label="Título"
            value={coerceString(data.heading)}
            onChange={(v) => setField("heading", v)}
          />
          <HtmlEditor
            label="Texto"
            value={coerceString(data.html)}
            onChange={(v) => setField("html", v)}
          />
          <TextField
            label="Cor de destaque (orange|red|amber|emerald|cyan)"
            value={coerceString(data.accent)}
            onChange={(v) => setField("accent", v)}
          />
        </div>
      );

    case "feature_cards":
      return (
        <div className="space-y-3">
          <TextField
            label="Título da secção"
            value={coerceString(data.heading)}
            onChange={(v) => setField("heading", v)}
          />
          <ObjectArrayEditor
            items={
              Array.isArray(data.items)
                ? (data.items as Record<string, unknown>[])
                : []
            }
            onChange={(items) => setField("items", items)}
            fields={[
              { key: "icon", label: "Ícone", type: "text" },
              { key: "title", label: "Título", type: "text" },
              { key: "description", label: "Descrição", type: "textarea" },
            ]}
            defaultItem={{ icon: "", title: "", description: "" }}
            itemLabel="Cartão"
          />
          <TextField
            label="Cor de destaque (orange|red|amber|emerald|cyan)"
            value={coerceString(data.accent)}
            onChange={(v) => setField("accent", v)}
          />
        </div>
      );

    default:
      console.warn(`[SectionFieldEditor] Unknown section type: ${type}`);
      return (
        <p className="text-sm text-gray-500 italic">
          Tipo de secção desconhecido: <code>{type}</code>
        </p>
      );
  }
}
